/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { gql } from '@apollo/client';

export const GET_WORKER_DOCUMENT = gql`
  query getWorkerDocument($workerId: ID!, $documentToken: String) {
    getWorkerDocument(workerId: $workerId, documentToken: $documentToken) {
      lineId
      text
      parentId
      shouldPause
      language
      metadata {
        lineId
        parentId
      }
      internalMetadata {
        key
        value
      }
    }
  }
`;

export const GET_SENTENCE_EXECUTION_DATA = gql`
  query getSentenceExecutionData(
    $workerId: ID!
    $token: String
    $documentToken: String
  ) {
    getSentenceExecutionData(
      workerId: $workerId
      iterationToken: $token
      documentToken: $documentToken
    ) {
      lineId
      token
      status
      answer
      concepts
      requests
      # subprocedureId
      iterationTokens
      epoch
      subDocuments {
        documentToken
        processName
      }
      miniPlaygrounds {
        documentToken
        processName
      }
      startedAt
      completedAt
    }
  }
`;

export const GET_DEPARTMENT_ID_FOR_WORKER = gql`
  query GetDepartmentForWorker($workerId: ID!) {
    getDepartmentForWorker(workerId: $workerId) {
      id
    }
  }
`;

export const GET_WORKER = gql`
  query GetWorker($id: ID!) {
    getWorker(id: $id) {
      id
      owner
      readers
      editors
      name
      description
      version
      departmentVersion
      knowledgeId
      departmentId
      procedureId
      entryCommand
      entryCommandId
      originalWorkerId
      createdAt
      state
      stage
      stateLastUpdatedAt
      attachments
      isArchived
      features
      replayWorkerId
    }
  }
`;

export const GET_WORKER_DEPENDENCIES = gql`
  query getWorkerDependencies($workerId: ID!) {
    getWorker(id: $workerId) {
      requests {
        limit
        nextToken
        items {
          id
          stepPath
          contextId
          sentenceId
          createdAt
          handledAt
          state
          worker {
            id
          }
          uiConcepts
          questions {
            id
            text
            contextId
            sentenceId
            answerTypeHint
            ... on NativeCodeErrorQuestion {
              errorType
              errorMessage
              errorTraceback
            }
            ... on ReviewConceptQuestion {
              lexicalPath
              conceptId
            }
            ... on ValueNotKnownQuestion {
              lexicalPath
              choices
              helpText
              isPlural
            }
          }
          answers {
            id
            requestId
            questionId
          }
          suggestedAnswers {
            id
            requestId
            questionId
            action {
              ... on ReplaceStepAction {
                _
                newSteps
              }
            }
          }
          contextPath {
            ctxId
            sentenceId
          }
        }
      }
    }
  }
`;

// Naming convention for Graphql queries is as per Apollo documention articles: 'SCREAMIGN_SNAKE_CASE'
export const LIST_WORKERS_BY_DEPARTMENT = gql`
  query listWorkersByDepartment(
    $departmentId: ID!
    $limit: Int
    $nextToken: String
  ) {
    listWorkersByDepartment(
      departmentId: $departmentId
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        owner
        name
        createdAt
        isArchived
        features
      }
      nextToken
    }
  }
`;

export const GET_STEP_PATH_FOR_RUN = gql`
  query GetStepPathForRun($workerId: ID!, $contextId: ID!, $stepId: ID!) {
    getStepPathForRun(
      workerId: $workerId
      contextId: $contextId
      stepId: $stepId
    )
  }
`;
